import {
  useAllPiiBackfillStates,
  usePauseShopsPiiBackfill,
  useStartShopsPiiBackfill,
} from "@/api/pii-vault/use-pii-backfill";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Pause, Play } from "lucide-react";
import { useMemo, useState } from "react";
import type { BackfillShop } from "./pii-backfill-shop-row";
import { PiiRepairRow } from "./pii-repair-row";
import {
  REPAIR_STATUSES,
  type RepairStatus,
  summarizeRepair,
} from "./pii-repair-status";

/** The API bounds one start request: each shop counts its documents first. */
const MAX_SHOPS_PER_START = 100;

/**
 * Super-admin launcher of the repair run: walks a backfilled shop again and
 * records the documents its first run left out of the vault. The backfill used
 * to skip documents sharing one creation date past a batch (bulk imports).
 * Documents already in the vault are skipped, so a repaired shop costs reads.
 */
export function PiiRepairTab({ shops }: { shops: BackfillShop[] }) {
  const overview = useAllPiiBackfillStates();
  const start = useStartShopsPiiBackfill();
  const pause = usePauseShopsPiiBackfill();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const rows = useMemo(() => {
    const states = overview.data?.states ?? [];
    return [...shops]
      .map((shop) => ({
        shop,
        summary: summarizeRepair(states.filter((s) => s.shopId === shop.id)),
      }))
      // Shops with a known gap first, then the lightest ones.
      .sort(
        (a, b) =>
          b.summary.missed - a.summary.missed ||
          a.shop.totalOrders - b.shop.totalOrders,
      );
  }, [shops, overview.data]);

  const counts = rows.reduce<Partial<Record<RepairStatus, number>>>(
    (acc, { summary }) => ({
      ...acc,
      [summary.status]: (acc[summary.status] ?? 0) + 1,
    }),
    {},
  );
  const shopIds = [...selected];
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectPending = () =>
    setSelected(
      new Set(
        rows
          .filter(({ summary }) => summary.status === "pending")
          .slice(0, MAX_SHOPS_PER_START)
          .map(({ shop }) => shop.id),
      ),
    );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Reparcourt un shop déjà rattrapé et range dans le coffre les documents
        que son premier passage a laissés de côté. Le backfill sautait les
        documents créés à la même date au-delà d'un lot, ce qui arrive après un
        import en masse. Les documents déjà dans le coffre sont sautés. La
        colonne Écart compte les documents comptés mais jamais parcourus : un
        écart à zéro ne garantit rien, les petits groupes n'y apparaissent pas.
        À terminer sur tous les shops avant de supprimer les données en clair.
      </p>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(REPAIR_STATUSES) as RepairStatus[]).map((status) => (
          <Badge key={status} variant="outline">
            {REPAIR_STATUSES[status]} : {counts[status] ?? 0}
          </Badge>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3">
        <Button size="sm" variant="outline" onClick={selectPending}>
          Sélectionner les shops à reprendre (max {MAX_SHOPS_PER_START})
        </Button>
        <Button
          size="sm"
          disabled={shopIds.length === 0 || start.isPending}
          onClick={() => start.mutate({ body: { shopIds, mode: "repair" } })}
        >
          <Play className="w-4 h-4" />
          Reprendre {shopIds.length} shop(s)
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={shopIds.length === 0 || pause.isPending}
          onClick={() => pause.mutate({ body: { shopIds } })}
        >
          <Pause className="w-4 h-4" />
          Pause
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-background">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2" />
              <th className="px-3 py-2">Shop</th>
              <th className="px-3 py-2 text-right">Commandes</th>
              <th className="px-3 py-2 text-right">Écart</th>
              <th className="px-3 py-2">Reprise</th>
              <th className="px-3 py-2">Progression</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ shop, summary }) => (
              <PiiRepairRow
                key={shop.id}
                shop={shop}
                summary={summary}
                isSelected={selected.has(shop.id)}
                onToggle={() => toggle(shop.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
